/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/no-unknown-property */
import React, { Fragment, useContext } from 'react'
import { Link } from 'react-router-dom'

import { ROUTE_TYPE } from '../../Configuration'
import { Inquiry } from '../../assets/ts/data/inquiry/inquiry'
import { STATUS_CODE } from '../../assets/ts/data/inquiry/inquiry-status-metadata'
import { CsvTitles } from '../../assets/ts/date-and-time/data-and-time.types'
import {
  convertDate,
  getTotalNumberOfDays,
} from '../../assets/ts/date-and-time/date-and-time'
import { defaultToEmptyString, twoDigits } from '../../assets/ts/formatting/formatting'
import { decodeHTML, handleScroll } from '../../assets/ts/general/general'
import { MetadataContext } from '../../store/metadata-context-provider'
import { SelectionContext } from '../../store/selection-context-provider'
import { INQUIRY_STATUS } from '../../utils/constants/global-constants'
import NO_RESULTS from '../../utils/constants/no-results'
import InputCheckbox from '../../utils/controls/input-checkbox'
import './table.css'

interface Props {
  csvTitle: CsvTitles
  inquiries: Inquiry[]
  onBottomScrollReached?: () => void
  onTopScrollReached?: () => void
}

const ArchivedInquiriesTable = ({
  csvTitle,
  inquiries,
  onBottomScrollReached,
  onTopScrollReached,
}: Props) => {
  const selection = useContext(SelectionContext)
  // to-do: there's some leftover dependency on inquiryStatusesObj from
  // the metadata context that no longer exists.  This has not yet been
  // analyzed
  const { inquiryStatuses } = useContext(MetadataContext)

  return (
    <div
      className='w-100 overflow-auto px-1 inquiriestable'id='scrolltable'
      onScroll={(e) =>
        handleScroll(e, onTopScrollReached, onBottomScrollReached)
      }
    >
      <table className='table-borderless table-dis w-100 sticky-top second-column last-column table-rowgap'>
        <thead>
          <tr>
            <th>
              <InputCheckbox
                formClassName='form-horizontal font-0'
                inputProps={{
                  checked: selection.allCheckboxIsChecked,
                  id: 'all',
                  label: 'All',
                  name: 'Input Check',
                  type: 'checkbox',
                }}
                onChange={selection.handleAllCheckboxChange}
              />
            </th>
            <th aria-label='Inquiry ID' tabIndex={0}>
              Inquiry ID
            </th>
            <th aria-label='Section' tabIndex={0}>
              Section
            </th>
            <th aria-label='Category' tabIndex={0}>
              Category
            </th>
            <th aria-label='Sub Category' tabIndex={0}>
              Sub Category
            </th>
            <th aria-label='Current Servicing Unit' tabIndex={0}>
              Current Servicing Unit
            </th>
            <th aria-label='Submitted Date' tabIndex={0}>
              Submitted Date
            </th>
            <th aria-label='Status' tabIndex={0}>
              Status
            </th>
            <th aria-label='Status Modified By' tabIndex={0}>
              Status Modified By
            </th>
            <th aria-label='Status Modified Date' tabIndex={0}>
              Status Modified Date
            </th>
            <th aria-label='# of Days' tabIndex={0}>
              # of Days
            </th>
          </tr>
        </thead>
        <tbody>{Row(inquiries, csvTitle, inquiryStatuses)}</tbody>
      </table>
      {inquiries?.length < 1 && NO_RESULTS}
    </div>
  )
}
function Row(inquiries: any, csvTitle: CsvTitles, inquiryStatuses: any) {
  const selection = useContext(SelectionContext)

  const row = inquiries?.map((inquiry: any) => {
    const guid = inquiry.InquiryGUID ?? ''
    return (
      <Fragment key={guid}>
        <tr>
          <th className='checkbox'>
            <InputCheckbox
              formClassName='form-horizontal font-0'
              inputProps={{
                checked: selection.keyIsSelected(guid),
                id: inquiry.key,
                label: `checkbox-${inquiry.InquiryID}`,
                name: 'Input Check',
                type: 'checkbox',
              }}
              onChange={(e) => {
                selection.handleSingleCheckboxChange(e, guid)
              }}
            />
          </th>
          <th aria-label={inquiry.InquiryID} tabIndex={0}>
            <Link
              className='links poppins-semibold font-11 title-color1'
              state={{ prevPath: "/archivedinquiries" }}
              title={inquiry.InquiryID}
              to={
                inquiry?.StatusCode === STATUS_CODE.SAVED
                  ? `${ROUTE_TYPE}inquiry/${inquiry.InquiryGUID}`
                  : `${ROUTE_TYPE}detailview/${inquiry.InquiryGUID}`
              }
            >
              {defaultToEmptyString(inquiry.InquiryID)}
            </Link>
          </th>
          <td aria-label={inquiry.SectionCode} tabIndex={0}>
            {defaultToEmptyString(inquiry.SectionValue)}
          </td>
          <td aria-label={inquiry.CategoryCode} tabIndex={0}>
            {defaultToEmptyString(inquiry.CategoryValue)}
          </td>
          <td aria-label={inquiry.SubCategoryCode} tabIndex={0}>
            {inquiry.SubCategoryValue}
          </td>
          <td aria-label={inquiry.CurrentlyServicingBy} tabIndex={0}>
            {inquiry.CurrentlyServicingBy}
          </td>
          <td aria-label={inquiry.ItemCreated} tabIndex={0}>
            {convertDate(inquiry.ItemCreated, 'newdate')}
          </td>
          <th
            aria-label={inquiry.StatusCode}
            data-table-status={INQUIRY_STATUS[inquiry.StatusCode]?.colorCode}
            tabIndex={0}
          >
            {defaultToEmptyString(inquiry.Status)}
          </th>
          <td
            aria-label={inquiry.ItemModifiedBy.properties.defaultDisplay}
            tabIndex={0}
          >
            {defaultToEmptyString(inquiry.ItemModifiedBy.properties.defaultDisplay)}
          </td>
          <td aria-label={inquiry.ItemModified} tabIndex={0}>
            {defaultToEmptyString(convertDate(inquiry.ItemModified, 'newdate'))}
          </td>
          <td aria-label='# of Days' tabIndex={0}>
            {defaultToEmptyString(twoDigits(
              getTotalNumberOfDays(
                csvTitle,
                false,
                inquiry,
                null,
                inquiryStatuses
              )
            )) || defaultToEmptyString(twoDigits(0))}
          </td>
        </tr>
        <tr>
          <th colSpan={12}>
            <div className='d-flex align-items-center'>
              <Link
                className='links poppins-semibold font-11 title-color46'
                state={{ prevPath: "/archivedinquiries" }}
                title={decodeHTML(inquiry.Subject)}
                to={
                  inquiry?.StatusCode === STATUS_CODE.SAVED
                    ? `${ROUTE_TYPE}/${inquiry.InquiryGUID}`
                    : `${ROUTE_TYPE}detailview/${inquiry.InquiryGUID}`
                }
              >
                {defaultToEmptyString(decodeHTML(inquiry.Subject))}
              </Link>
              <div className='dividerdashed vertical mx-2'></div>
              <div
                aria-label={inquiry.username}
                className='m-0 p-0 userinfo title-color15 font-11 text-uppercase poppins-semibold'
                tabIndex={0}
              >
                <span className='icon-user '></span>
                {defaultToEmptyString(inquiry.Customer.properties.defaultDisplay)}
              </div>
              <div className='d-flex ms-auto'>
                <div
                  aria-label={inquiry.statuslabel}
                  className={inquiry.statuslabelclass}
                  tabIndex={0}
                >
                  {defaultToEmptyString(inquiry.statuslabel)}
                </div>
              </div>
            </div>
          </th>
        </tr>
      </Fragment>
    )
  })
  if (inquiries?.length > 0) {
    return row
  } else {
    return (
      <tr className='no-results'>
        <td colSpan='25'>
          <NO_RESULTS></NO_RESULTS>
        </td>
      </tr>
    )
  }
}

export default ArchivedInquiriesTable
